import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { 
  ArrowLeft, 
  Loader2, 
  FileText,
  X,
  Save,
  XCircle
} from 'lucide-react';
import { grnApiService, GRNDetail } from '../services/grnApi';
import { fileUploadApiService } from '../services/fileUploadApi';
import { UPLOAD_FOLDERS, API_ENDPOINTS, API_CONFIG } from '../config/api';

export function AccessDocPage() {
  const { grnId } = useParams<{ grnId: string }>();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [grnNumber, setGrnNumber] = useState('');
  const [items, setItems] = useState<GRNDetail[]>([]);
  const [editedItems, setEditedItems] = useState<Record<number, {
    tcNo?: string;
    tcDate?: string;
    tcDocumentPath?: string | null;
  }>>({});
  const [originalItems, setOriginalItems] = useState<Record<number, {
    tcNo?: string;
    tcDate?: string;
  }>>({});
  const [uploadingFiles, setUploadingFiles] = useState<Record<number, boolean>>({});
  const [savingItems, setSavingItems] = useState<Record<number, boolean>>({});

  useEffect(() => {
    if (grnId) {
      loadGRNDetails();
    }
  }, [grnId]);

  const loadGRNDetails = async () => {
    if (!grnId) return;
    
    try {
      setLoading(true);
      
      // Get GRN details (items) - each item contains the GRN object
      const grnDetails = await grnApiService.getGRNDetailsByGRN(parseInt(grnId));
      
      // Extract GRN Number from the first item's GRN object
      // (all items have the same GRN, so we can use the first one)
      if (grnDetails.length > 0 && grnDetails[0].grn) {
        setGrnNumber(grnDetails[0].grn.grnNumber);
      } else {
        setGrnNumber('N/A');
      }
      
      setItems(grnDetails);
      
      // Initialize edited items with current values
      const initialEdited: Record<number, {
        tcNo?: string;
        tcDate?: string;
        tcDocumentPath?: string | null;
      }> = {};
      
      const initialOriginal: Record<number, {
        tcNo?: string;
        tcDate?: string;
      }> = {};
      
      grnDetails.forEach(item => {
        const tcNo = item.tcNo || '';
        const tcDate = item.tcDate ? item.tcDate.split(' ')[0] : ''; // Extract date part only
        
        initialEdited[item.id] = {
          tcNo,
          tcDate,
          tcDocumentPath: item.tcDocumentPath || null
        };
        
        initialOriginal[item.id] = {
          tcNo,
          tcDate
        };
      });
      
      setEditedItems(initialEdited);
      setOriginalItems(initialOriginal);
    } catch (error) {
      // console.error('Error loading GRN details:', error);
      toast.error('Failed to load GRN details');
    } finally {
      setLoading(false);
    }
  };


  const handleFileUpload = async (itemId: number, file: File) => {
    const item = items.find(i => i.id === itemId);
    if (!item) {
      toast.error('Item not found');
      return;
    }

    // Heat No is required for TC document upload
    if (!item.heatNo) {
      toast.error('Heat No is required to upload TC document');
      return;
    }

    setUploadingFiles(prev => ({ ...prev, [itemId]: true }));

    try {
      // Build dynamic folder path: po_receive/supplier-lab-report/<heatNo>
      const folderPath = `${UPLOAD_FOLDERS.GRN_SUPPLIER_LAB_REPORT}/${item.heatNo}`;
      
      // Upload file with proper folder structure
      const result = await fileUploadApiService.uploadFile(
        file,
        'document',
        undefined, // onProgress
        folderPath,
        true // keepOriginalName
      );
      
      const filePath = result.filePath || result.fileKey || '';
      const fileName = result.originalName || result.fileName || file.name;
      
      // Get current edited values for this item
      const edited = editedItems[itemId] || {};
      
      // Update only TC document path, TC No, and TC Date
      // Keep all other information the same as before
      await grnApiService.updateGRNDetail(itemId.toString(), {
        id: item.id,
        lineNumber: item.lineNumber,
        quantity: item.quantity,
        receivedQuantity: item.receivedQuantity,
        unitPrice: item.unitPrice,
        lineTotal: item.lineTotal, // Use existing lineTotal, don't recalculate
        qualityCheck: item.qualityCheck || '',
        remarks: item.remarks || '',
        heatNo: item.heatNo || '',
        tcNo: edited.tcNo || null, // Only update TC No
        tcDate: edited.tcDate ? edited.tcDate + ' 00:00:00' : null, // Only update TC Date
        tcDocumentPath: filePath, // Only update TC document path
        supplierHeatNo: item.supplierHeatNo || '',
        nop: item.nop, // Preserve existing nop
        branchId: item.branchId,
        storeId: item.storeId,
        grnId: item.grn.id,
        rawItemId: item.rawItem.id,
        createdBy: item.createdBy,
        updatedBy: item.updatedBy
      });
      
      // Update local state with file path
      setEditedItems(prev => ({
        ...prev,
        [itemId]: {
          ...prev[itemId],
          tcDocumentPath: filePath
        }
      }));
      
      // Update items state
      setItems(prev => prev.map(i => 
        i.id === itemId 
          ? { ...i, tcDocumentPath: filePath, tcNo: edited.tcNo || '', tcDate: edited.tcDate ? edited.tcDate + ' 00:00:00' : '' }
          : i
      ));
      
      toast.success(`TC document uploaded for ${item.rawItem?.itemName || 'item'}`);
    } catch (error) {
      // console.error('Error uploading TC document:', error);
      toast.error('Failed to upload TC document');
    } finally {
      setUploadingFiles(prev => {
        const { [itemId]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const getFileName = (filePath: string): string => {
    if (!filePath) return '';
    // Get the last part after splitting by '/'
    const parts = filePath.split('/');
    return parts[parts.length - 1] || filePath;
  };

  const handleDownloadFile = async (filePath: string) => {
    if (!filePath) return;
    
    try {
      // Get JWT token from localStorage
      const token = localStorage.getItem('authToken');
      const params = new URLSearchParams({
        fileKey: filePath
      });
      const downloadUrl = `${API_CONFIG.BASE_URL}${API_ENDPOINTS.FILES.DOWNLOAD_FILE}?${params.toString()}`;
      
      // Build headers with JWT token
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      // Fetch file with authentication
      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: headers,
      });
      
      if (!response.ok) {
        if (response.status === 403) {
          toast.error('Access denied. Please check your permissions.');
        } else if (response.status === 401) {
          toast.error('Session expired. Please login again.');
        } else {
          toast.error(`Failed to download file: ${response.statusText}`);
        }
        return;
      }
      
      // Get the blob from response
      const blob = await response.blob();
      
      // Create a blob URL and trigger download on same page (not in new tab)
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = getFileName(filePath);
      // Don't set target='_blank' to avoid opening in new tab
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the blob URL
      window.URL.revokeObjectURL(blobUrl);
      
      toast.success('File downloaded successfully');
    } catch (error: any) {
      // console.error('Error downloading file:', error);
      toast.error(`Failed to download file: ${error.message || 'Unknown error'}`);
    }
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return dateString;
    }
  };

  const hasChanges = (itemId: number): boolean => {
    const edited = editedItems[itemId] || {};
    const original = originalItems[itemId] || {};
    return (edited.tcNo || '') !== (original.tcNo || '') || 
           (edited.tcDate || '') !== (original.tcDate || '');
  };

  const handleSaveTC = async (itemId: number) => {
    const item = items.find(i => i.id === itemId);
    if (!item) {
      toast.error('Item not found');
      return;
    }

    setSavingItems(prev => ({ ...prev, [itemId]: true }));

    try {
      const edited = editedItems[itemId] || {};
      
      // Use all existing information from GRN detail, only update tcNo and tcDate
      await grnApiService.updateGRNDetail(itemId.toString(), {
        id: item.id,
        lineNumber: item.lineNumber,
        quantity: item.quantity,
        receivedQuantity: item.receivedQuantity,
        unitPrice: item.unitPrice,
        lineTotal: item.lineTotal, // Use existing lineTotal
        qualityCheck: item.qualityCheck || '',
        remarks: item.remarks || '',
        heatNo: item.heatNo || '',
        tcNo: edited.tcNo || null, // Only update tcNo from edited values
        tcDate: edited.tcDate ? edited.tcDate + ' 00:00:00' : null, // Only update tcDate from edited values
        tcDocumentPath: item.tcDocumentPath || null, // Use existing tcDocumentPath
        supplierHeatNo: item.supplierHeatNo || '',
        nop: item.nop, // Include nop if it exists
        branchId: item.branchId,
        storeId: item.storeId,
        grnId: item.grn.id,
        rawItemId: item.rawItem.id,
        createdBy: item.createdBy,
        updatedBy: item.updatedBy
      });

      // Update original values to match edited values
      setOriginalItems(prev => ({
        ...prev,
        [itemId]: {
          tcNo: edited.tcNo || '',
          tcDate: edited.tcDate || ''
        }
      }));

      // Update items state
      setItems(prev => prev.map(i => 
        i.id === itemId 
          ? { 
              ...i, 
              tcNo: edited.tcNo || '', 
              tcDate: edited.tcDate ? edited.tcDate + ' 00:00:00' : '' 
            }
          : i
      ));

      toast.success('TC No and TC Date saved successfully');
    } catch (error) {
      // console.error('Error saving TC No and TC Date:', error);
      toast.error('Failed to save TC No and TC Date');
    } finally {
      setSavingItems(prev => {
        const { [itemId]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleCancelTC = (itemId: number) => {
    const original = originalItems[itemId] || {};
    setEditedItems(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        tcNo: original.tcNo || '',
        tcDate: original.tcDate || ''
      }
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
        <span className="ml-2">Loading GRN details...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
        <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/grn')}
                className="text-white hover:bg-white/20"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div className="h-8 w-8 rounded-lg bg-primary-200 flex items-center justify-center">
                <FileText className="h-5 w-5 text-primary-700" />
              </div>
              <div>
                <CardTitle className="text-xl text-gray-900">Access Documents</CardTitle>
                <p className="text-gray-600 text-sm">Manage TC documents for GRN items</p>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {/* GRN Info */}
          <div className="mb-6">
            <div className="space-y-1">
              <Label className="text-sm text-gray-600">GRN Number</Label>
              <div className="text-lg font-semibold text-gray-900">{grnNumber}</div>
            </div>
          </div>

          {/* Items Table */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                  <TableHead>Item Name</TableHead>
                  <TableHead>Heat No</TableHead>
                  <TableHead>TC No</TableHead>
                  <TableHead>TC Date</TableHead>
                  <TableHead>TC Document</TableHead>
                  <TableHead className="text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                      No items found
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item) => {
                    const edited = editedItems[item.id] || {};
                    
                    return (
                      <TableRow key={item.id}>
                        <TableCell className="font-medium">
                          {item.rawItem?.itemName || 'N/A'}
                        </TableCell>
                        <TableCell>
                          {item.heatNo || 'N/A'}
                        </TableCell>
                        <TableCell>
                          <Input
                            value={edited.tcNo || ''}
                            onChange={(e) => {
                              setEditedItems(prev => ({
                                ...prev,
                                [item.id]: {
                                  ...prev[item.id],
                                  tcNo: e.target.value
                                }
                              }));
                            }}
                            placeholder="Enter TC No"
                            className="w-32"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="date"
                            value={edited.tcDate || ''}
                            onChange={(e) => {
                              setEditedItems(prev => ({
                                ...prev,
                                [item.id]: {
                                  ...prev[item.id],
                                  tcDate: e.target.value
                                }
                              }));
                            }}
                            className="w-40"
                          />
                        </TableCell>
                        <TableCell>
                          {item.tcDocumentPath ? (
                            /* Uploaded file */
                            <div className="flex items-center gap-2 p-2 bg-white border border-gray-200 rounded-md w-full">
                              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded flex items-center justify-center">
                                <FileText className="h-4 w-4 text-blue-600" />
                              </div>
                              <button
                                onClick={() => handleDownloadFile(item.tcDocumentPath!)}
                                className="flex-1 min-w-0 text-sm text-blue-600 hover:text-blue-800 hover:underline truncate text-left"
                                title={`Download ${getFileName(item.tcDocumentPath)}`}
                              >
                                {getFileName(item.tcDocumentPath)}
                              </button>
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                onClick={async () => {
                                  // Remove TC document
                                  try {
                                    setSaving(true);
                                    const edited = editedItems[item.id] || {};
                                    await grnApiService.updateGRNDetail(item.id.toString(), {
                                      id: item.id,
                                      lineNumber: item.lineNumber,
                                      quantity: item.quantity,
                                      receivedQuantity: item.receivedQuantity,
                                      unitPrice: item.unitPrice,
                                      lineTotal: item.receivedQuantity * item.unitPrice,
                                      qualityCheck: item.qualityCheck,
                                      remarks: item.remarks,
                                      heatNo: item.heatNo,
                                      tcNo: edited.tcNo || null,
                                      tcDate: edited.tcDate ? edited.tcDate + ' 00:00:00' : null,
                                      tcDocumentPath: null,
                                      supplierHeatNo: item.supplierHeatNo,
                                      branchId: item.branchId,
                                      storeId: item.storeId,
                                      grnId: item.grn.id,
                                      rawItemId: item.rawItem.id,
                                      createdBy: item.createdBy,
                                      updatedBy: item.updatedBy
                                    });
                                    
                                    // Update local state
                                    setEditedItems(prev => ({
                                      ...prev,
                                      [item.id]: {
                                        ...prev[item.id],
                                        tcDocumentPath: null
                                      }
                                    }));
                                    
                                    setItems(prev => prev.map(i => 
                                      i.id === item.id ? { ...i, tcDocumentPath: null } : i
                                    ));
                                    
                                    toast.success('TC document removed');
                                  } catch (error) {
                                    // console.error('Error removing TC document:', error);
                                    toast.error('Failed to remove TC document');
                                  } finally {
                                    setSaving(false);
                                  }
                                }}
                                className="h-6 w-6 p-0 text-red-600 hover:text-red-700 hover:bg-red-50 flex-shrink-0"
                                title="Remove file"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ) : (
                            /* Upload button */
                            <div className="flex items-center">
                              <input
                                type="file"
                                id={`tc-doc-${item.id}`}
                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    handleFileUpload(item.id, file);
                                  }
                                  e.target.value = '';
                                }}
                                className="hidden"
                                disabled={uploadingFiles[item.id]}
                              />
                              <label
                                htmlFor={`tc-doc-${item.id}`}
                                className={`flex items-center gap-2 px-3 py-1.5 text-xs border border-primary-200 rounded-md cursor-pointer hover:bg-primary-50 ${
                                  uploadingFiles[item.id] ? 'opacity-50 cursor-not-allowed' : ''
                                }`}
                              >
                                {uploadingFiles[item.id] ? (
                                  <>
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                    <span>Uploading...</span>
                                  </>
                                ) : (
                                  <>
                                    <FileText className="h-3 w-3" />
                                    <span>Upload TC Doc</span>
                                  </>
                                )}
                              </label>
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-center gap-2">
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => handleSaveTC(item.id)}
                              disabled={savingItems[item.id] || !hasChanges(item.id)}
                              className={`h-8 w-8 p-0 ${
                                hasChanges(item.id)
                                  ? 'text-green-600 hover:text-green-700 hover:bg-green-50'
                                  : 'text-gray-400 cursor-not-allowed opacity-50'
                              }`}
                              title={hasChanges(item.id) ? 'Save TC No and TC Date' : 'No changes to save'}
                            >
                              {savingItems[item.id] ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Save className="h-4 w-4" />
                              )}
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => handleCancelTC(item.id)}
                              disabled={savingItems[item.id] || !hasChanges(item.id)}
                              className={`h-8 w-8 p-0 ${
                                hasChanges(item.id)
                                  ? 'text-red-600 hover:text-red-700 hover:bg-red-50'
                                  : 'text-gray-400 cursor-not-allowed opacity-50'
                              }`}
                              title={hasChanges(item.id) ? 'Cancel changes' : 'No changes to cancel'}
                            >
                              <XCircle className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

